/**
 * Maps each public pricing tier to its Stripe Price ID and what it means for
 * enrollment. `priceId` starts blank for tiers Cassie hasn't sent us yet —
 * the "Enroll Now" button for a tier only renders once its priceId is set,
 * so nothing on the public pricing page breaks while we're still filling
 * these in.
 *
 * `packageType` mirrors the values used by the existing admin package
 * catalog (in_person | online | meal_plan), used for display only here.
 */
export interface StripePackageConfig {
  priceId: string
  mode: 'subscription' | 'payment'
  packageName: string
  packageType: 'in_person' | 'online' | 'meal_plan'
  durationDays: number
  priceLabel: string
}

export const STRIPE_PRICE_MAP: Record<string, StripePackageConfig> = {
  strongAgain: {
    priceId: 'price_1UMHz2H6bg0o3y2RKyIh29Ac',
    mode: 'subscription',
    packageName: 'The Strong Again Method™',
    packageType: 'online',
    durationDays: 90,
    priceLabel: '$347/month',
  },
  'strongAgain-paidInFull': {
    priceId: 'price_1UMI32H6bg0o3y2RNfVQzb1x',
    mode: 'payment',
    packageName: 'The Strong Again Method™',
    packageType: 'online',
    durationDays: 90,
    priceLabel: '$1,041 (3-month commitment, paid in full)',
  },

  nutritionBlueprint: {
    priceId: 'price_1UMICQH6bg0o3y2RDJy4XmpP',
    mode: 'payment',
    packageName: 'Nutrition Blueprint',
    packageType: 'meal_plan',
    durationDays: 84,
    priceLabel: '$349',
  },

  'championsBlueprint-12w': {
    priceId: 'price_1UMIJuH6bg0o3y2RCMovZ7bt',
    mode: 'payment',
    packageName: "Champion's Blueprint™",
    packageType: 'in_person',
    durationDays: 84,
    priceLabel: '$900',
  },
  'championsBlueprint-14w': {
    priceId: 'price_1UMILjH6bg0o3y2RipzmK7Cl',
    mode: 'payment',
    packageName: "Champion's Blueprint™",
    packageType: 'in_person',
    durationDays: 98,
    priceLabel: '$1,050',
  },
  'championsBlueprint-16w': {
    priceId: 'price_1UMIN5H6bg0o3y2R4umNeoXB',
    mode: 'payment',
    packageName: "Champion's Blueprint™",
    packageType: 'in_person',
    durationDays: 112,
    priceLabel: '$1,200',
  },

  'proLook-12w': {
    priceId: 'price_1UMIQjH6bg0o3y2RxLdYMSYf',
    mode: 'payment',
    packageName: 'The Pro Look™',
    packageType: 'in_person',
    durationDays: 84,
    priceLabel: '$799',
  },
  'proLook-14w': {
    priceId: 'price_1UMISwH6bg0o3y2Ryj2dTwfT',
    mode: 'payment',
    packageName: 'The Pro Look™',
    packageType: 'in_person',
    durationDays: 98,
    priceLabel: '$925',
  },
  'proLook-16w': {
    priceId: 'price_1UMITsH6bg0o3y2RIg6CuqLW',
    mode: 'payment',
    packageName: 'The Pro Look™',
    packageType: 'in_person',
    durationDays: 112,
    priceLabel: '$1,050',
  },

  'eliteCompetitiveEdge-12w': {
    priceId: 'price_1UMIVPH6bg0o3y2RcR5a6oIk',
    mode: 'payment',
    packageName: 'Elite Competitive Edge™',
    packageType: 'online',
    durationDays: 84,
    priceLabel: '$749',
  },
  'eliteCompetitiveEdge-16w': {
    priceId: 'price_1UQG8SH6bg0o3y2RQ5K2R2Vl',
    mode: 'payment',
    packageName: 'Elite Competitive Edge™',
    packageType: 'online',
    durationDays: 112,
    priceLabel: '$949',
  },
  'eliteCompetitiveEdge-24w': {
    priceId: 'price_1UMIWtH6bg0o3y2RD4uRE2Vg',
    mode: 'payment',
    packageName: 'Elite Competitive Edge™',
    packageType: 'online',
    durationDays: 168,
    priceLabel: '$1,399',
  },
}
